import React, { useState } from "react";
import { Field } from "formik";

import {
  FormControl,
  StyledField,
  ErrorMessage,
  StyledSelect,
  Label,
} from "../../Form/FormControls";

const authMethodOptions = [
  { value: "KEY_PAIR", label: "Key Pair" },
  { value: "PROGRAMMATIC_TOKEN", label: "Programmatic Access Token" },
];

const SnowflakeConnection = ({ form }) => {
  const [showPassword, setShowPassword] = useState(false);

  const authMethod =
    form.values.details?.snowflakeConnectionString?.authenticationMethod;

  const handleAuthMethodChange = (e) => {
    const current = form.values.details.snowflakeConnectionString;
    form.setFieldValue("details.snowflakeConnectionString", {
      ...current,
      authenticationMethod: e.value,
      username: null,
      privateKey: null,
      passphrase: null,
      accessToken: null,
    });
  };

  return (
    <>
      <h3>Connection Details</h3>
      <FormControl>
        <label>
          Use Advanced Connection String
          <Field
            type="checkbox"
            checked={form.values.advanced}
            name={`advanced`}
            onChange={() => {
              form.setFieldValue(`advanced`, !form.values.advanced);
              form.setFieldValue(`details`, {
                snowflakeConnectionString: {
                  connectionString: null,
                  authenticationMethod: null,
                  accountIdentifier: null,
                  warehouse: null,
                  database: null,
                  schema: null,
                  role: null,
                  username: null,
                  privateKey: null,
                  passphrase: null,
                  accessToken: null,
                },
                databaseConnectionString: null,
                azureStorageConnectionString: null,
                sftpConnectionString: null,
                cosmosConnectionString: null,
                dataverseConnectionString: null,
                cubeConnectionString: null,
                ftpsConnectionString: null,
              });
            }}
          />
        </label>
      </FormControl>

      {form.values.advanced ? (
        <FormControl>
          <StyledField
            name={`details.snowflakeConnectionString.connectionString`}
            type="text"
            placeholder="Connection String"
            label="Connection String"
          />
          <ErrorMessage
            name={`details.snowflakeConnectionString.connectionString`}
          />
        </FormControl>
      ) : (
        <>
          <FormControl>
            <StyledField
              name={`details.snowflakeConnectionString.accountIdentifier`}
              type="text"
              placeholder="Account Identifier (e.g., xy12345.us-east-1)"
              label="Account Identifier"
            />
            <ErrorMessage
              name={`details.snowflakeConnectionString.accountIdentifier`}
            />
          </FormControl>

          <FormControl>
            <StyledField
              name={`details.snowflakeConnectionString.warehouse`}
              type="text"
              placeholder="Warehouse"
              label="Warehouse"
            />
            <ErrorMessage
              name={`details.snowflakeConnectionString.warehouse`}
            />
          </FormControl>

          <FormControl>
            <StyledField
              name={`details.snowflakeConnectionString.database`}
              type="text"
              placeholder="Database"
              label="Database"
            />
            <ErrorMessage
              name={`details.snowflakeConnectionString.database`}
            />
          </FormControl>

          <FormControl>
            <StyledField
              name={`details.snowflakeConnectionString.schema`}
              type="text"
              placeholder="Schema (optional)"
              label="Schema"
            />
            <small>
              Optional. When set, BaseCap only browses tables in this schema.
              Leave blank to browse every schema in the database.
            </small>
            <ErrorMessage
              name={`details.snowflakeConnectionString.schema`}
            />
          </FormControl>

          <FormControl>
            <StyledField
              name={`details.snowflakeConnectionString.role`}
              type="text"
              placeholder="Role (e.g., BASECAP_READER)"
              label="Role"
            />
            <small>
              The Snowflake role this connection assumes. Controls which
              databases, schemas, and tables BaseCap can see.
            </small>
            <ErrorMessage
              name={`details.snowflakeConnectionString.role`}
            />
          </FormControl>

          <FormControl>
            <Label>Authentication Method</Label>
            <StyledSelect
              className={`react-select-container`}
              classNamePrefix={`react-select`}
              name={`authenticationMethod`}
              placeholder={`Select Authentication Method`}
              value={authMethodOptions.find((o) => o.value === authMethod)}
              options={authMethodOptions}
              onChange={handleAuthMethodChange}
            />
            <ErrorMessage
              name={`details.snowflakeConnectionString.authenticationMethod`}
            />
          </FormControl>

          {authMethod === "KEY_PAIR" && (
            <>
              <FormControl>
                <StyledField
                  name={`details.snowflakeConnectionString.username`}
                  type="text"
                  placeholder="Username"
                  label="Username"
                />
                <ErrorMessage
                  name={`details.snowflakeConnectionString.username`}
                />
              </FormControl>

              <FormControl>
                <label>Private Key (PEM)</label>
                <Field
                  as="textarea"
                  name={`details.snowflakeConnectionString.privateKey`}
                  placeholder="Paste PEM-encoded private key"
                  rows={6}
                  style={{ width: "100%", fontFamily: "monospace" }}
                />
                <ErrorMessage
                  name={`details.snowflakeConnectionString.privateKey`}
                />
              </FormControl>

              <FormControl>
                <StyledField
                  name={`details.snowflakeConnectionString.passphrase`}
                  type={showPassword ? "text" : "password"}
                  placeholder="Passphrase (optional)"
                  label="Passphrase"
                />
                <label>
                  Show Passphrase
                  <Field
                    type="checkbox"
                    checked={showPassword}
                    name={`showPassword`}
                    onChange={() => setShowPassword((prev) => !prev)}
                  />
                </label>
                <ErrorMessage
                  name={`details.snowflakeConnectionString.passphrase`}
                />
              </FormControl>
            </>
          )}

          {authMethod === "PROGRAMMATIC_TOKEN" && (
            <>
              <FormControl>
                <StyledField
                  name={`details.snowflakeConnectionString.username`}
                  type="text"
                  placeholder="Username"
                  label="Username"
                />
                <small>
                  The Snowflake user that owns the access token.
                </small>
                <ErrorMessage
                  name={`details.snowflakeConnectionString.username`}
                />
              </FormControl>

              <FormControl>
                <StyledField
                  name={`details.snowflakeConnectionString.accessToken`}
                  type={showPassword ? "text" : "password"}
                  placeholder="Access Token"
                  label="Access Token"
                />
                <label>
                  Show Token
                  <Field
                    type="checkbox"
                    checked={showPassword}
                    name={`showPassword`}
                    onChange={() => setShowPassword((prev) => !prev)}
                  />
                </label>
                <ErrorMessage
                  name={`details.snowflakeConnectionString.accessToken`}
                />
              </FormControl>
            </>
          )}
        </>
      )}
    </>
  );
};

export default SnowflakeConnection;
